import { useState } from 'react';
import { Link } from 'react-router-dom';
import { PrivacySettings } from '../common/PrivacySettings';

/**
 * Footer Component
 * Enhanced footer with SEO-friendly content, navigation links, and privacy settings
 */

/**
 * Footer component
 * Displays site navigation, creator info, copyright, and privacy controls
 * 
 * @returns Footer component
 */
export function Footer() {
  const currentYear = new Date().getFullYear();
  const [isPrivacySettingsOpen, setIsPrivacySettingsOpen] = useState(false);

  return (
    <footer 
      className="site-footer"
      style={{
        width: '100%',
        background: 'var(--color-surface)',
        color: 'var(--color-fg)',
        fontFamily: 'var(--font-family)',
        borderTop: '4px solid var(--color-accent)',
        marginTop: 'auto',
        boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.3)'
      }}
    >
      <div style={{ 
        maxWidth: '1200px', 
        margin: '0 auto', 
        padding: '2.5rem 1.5rem 1.5rem'
      }}>
        {/* Main Footer Content */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '2rem',
          marginBottom: '2rem'
        }}>
          {/* About Section */}
          <div>
            <h3 style={{
              color: 'var(--color-accent)',
              fontSize: 'var(--font-size-sm)',
              marginBottom: '1rem',
              fontWeight: 'bold'
            }}>
              About 8-Bit Composer
            </h3>
            <p style={{
              fontSize: '0.75rem',
              lineHeight: '1.6',
              marginBottom: '0.75rem',
              opacity: 0.9
            }}>
              Create retro chiptune music in your browser. Free online music maker with 8-bit synthesizers, 
              drum machines, and sequencer for composing nostalgic video game soundtracks.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 style={{
              color: 'var(--color-accent)',
              fontSize: 'var(--font-size-sm)',
              marginBottom: '1rem',
              fontWeight: 'bold'
            }}>
              Quick Links
            </h3>
            <nav style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem'
            }}>
              <Link
                to="/"
                style={{
                  color: 'var(--color-fg)',
                  textDecoration: 'none',
                  fontSize: '0.75rem',
                  transition: 'color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = 'var(--color-accent)'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'var(--color-fg)'}
              >
                🎵 Composer
              </Link>
              <Link
                to="/about"
                style={{
                  color: 'var(--color-fg)',
                  textDecoration: 'none',
                  fontSize: '0.75rem',
                  transition: 'color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = 'var(--color-accent)'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'var(--color-fg)'}
              >
                ℹ️ About
              </Link>
              <Link
                to="/contact"
                style={{
                  color: 'var(--color-fg)',
                  textDecoration: 'none',
                  fontSize: '0.75rem',
                  transition: 'color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = 'var(--color-accent)'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'var(--color-fg)'}
              >
                📧 Contact
              </Link>
              <Link
                to="/faq"
                style={{
                  color: 'var(--color-fg)',
                  textDecoration: 'none',
                  fontSize: '0.75rem',
                  transition: 'color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = 'var(--color-accent)'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'var(--color-fg)'}
              >
                ❓ FAQ
              </Link>
              <Link
                to="/terms"
                style={{
                  color: 'var(--color-fg)',
                  textDecoration: 'none',
                  fontSize: '0.75rem',
                  transition: 'color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = 'var(--color-accent)'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'var(--color-fg)'}
              >
                📜 Terms & Ownership
              </Link>
            </nav>
          </div>

          {/* Community */}
          <div>
            <h3 style={{
              color: 'var(--color-accent)',
              fontSize: 'var(--font-size-sm)',
              marginBottom: '1rem',
              fontWeight: 'bold'
            }}>
              Community
            </h3>
            <nav style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem'
            }}>
              <a
                href="https://discord.gg/NDHSpd7be5"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: 'var(--color-fg)',
                  textDecoration: 'none',
                  fontSize: '0.75rem',
                  transition: 'color 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(88, 101, 242, 1)'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'var(--color-fg)'}
              >
                💬 Discord Server
              </a>
              <p style={{
                fontSize: '0.7rem',
                lineHeight: '1.5',
                opacity: 0.8,
                margin: '0.5rem 0 0 0'
              }}>
                Join our community to share compositions, get help, and connect with other creators!
              </p>
            </nav>
          </div>

          {/* Legal & Privacy */}
          <div>
            <h3 style={{
              color: 'var(--color-accent)',
              fontSize: 'var(--font-size-sm)',
              marginBottom: '1rem',
              fontWeight: 'bold'
            }}>
              Legal & Privacy
            </h3>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem'
            }}>
              <button
                onClick={() => setIsPrivacySettingsOpen(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--color-fg)',
                  cursor: 'pointer',
                  fontSize: '0.75rem',
                  fontFamily: 'var(--font-family)',
                  padding: '0',
                  margin: '0',
                  textAlign: 'left',
                  display: 'block',
                  lineHeight: 'inherit',
                  transition: 'color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = 'var(--color-accent)'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'var(--color-fg)'}
                aria-label="Privacy settings"
              >
                🔒 Privacy Settings
              </button>
              <Link
                to="/privacy"
                style={{
                  color: 'var(--color-fg)',
                  textDecoration: 'none',
                  fontSize: '0.75rem',
                  transition: 'color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = 'var(--color-accent)'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'var(--color-fg)'}
              >
                📄 Privacy Policy
              </Link>
              <p style={{
                fontSize: '0.7rem',
                lineHeight: '1.5',
                opacity: 0.8,
                margin: '0.5rem 0 0 0'
              }}>
                Your privacy matters. Control your data and analytics preferences.
              </p>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div style={{
          borderTop: '1px solid var(--color-accent)',
          opacity: 0.3,
          marginBottom: '1.5rem'
        }} />

        {/* Bottom Bar */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '0.75rem',
          fontSize: '0.7rem',
          textAlign: 'center'
        }}>
          <p style={{ margin: 0, opacity: 0.9 }}>
            Powered by pixels and patterns ✨
          </p>
          <p style={{ margin: 0 }}>
            Crafted with love by{' '}
            <a
              href="https://doriansmith.dev"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: 'var(--color-accent)',
                textDecoration: 'none',
                fontWeight: 'bold'
              }}
              onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
              onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
            >
              Dorian Smith
            </a>
          </p>
          <p style={{ margin: 0, opacity: 0.8 }}>
            {currentYear} © All bleeps and bloops reserved.
          </p>
        </div>
      </div>

      {/* Privacy Settings Modal */}
      <PrivacySettings
        isOpen={isPrivacySettingsOpen}
        onClose={() => setIsPrivacySettingsOpen(false)}
      />
    </footer>
  );
}
